import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useTabs, type OpenedTab } from '../store/tabs'

interface ActorHit {
  did: string
  handle: string
  displayName?: string
  avatar?: string
}

/**
 * Full-screen search for the mobile shell. The input is pinned to the
 * TOP so the software keyboard (which rises from the bottom) never
 * covers the results. Mirrors SearchBox's logic (handle typeahead +
 * post-search mode) but lives separately — desktop shares SearchBox and
 * its inline dropdown doesn't survive a phone keyboard.
 */
export function MobileSearchModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const addTab = useTabs((s) => s.open)
  const tabs = useTabs((s) => s.tabs)

  const [q, setQ] = useState('')
  const [hits, setHits] = useState<ActorHit[]>([])
  const [searching, setSearching] = useState(false)
  // When on, switches from "jump to a handle" to a full-text POST
  // search: submitting navigates to /tabs-search and the live handle
  // typeahead is suppressed (same semantics as SearchBox).
  const [postSearch, setPostSearch] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (postSearch) { setHits([]); return }
    if (!q.trim()) { setHits([]); return }
    debounceRef.current = setTimeout(() => {
      setSearching(true)
      // Typeahead is a PUBLIC read — hit the AppView directly so an
      // expired/deactivated session can't silently empty the list.
      void api
        .appView<{ actors?: ActorHit[] }>({
          path: `app.bsky.actor.searchActorsTypeahead?q=${encodeURIComponent(
            q.trim().replace(/^@+/, ''),
          )}&limit=8`,
        })
        .then((r) => setHits(r.actors ?? []))
        .catch(() => setHits([]))
        .finally(() => setSearching(false))
    }, 200)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [q, postSearch])

  function pickActor(a: ActorHit) {
    addTab({ handle: a.handle, did: a.did, displayName: a.displayName ?? null, avatar: a.avatar ?? null })
    navigate(`/profile/${encodeURIComponent(a.handle)}`)
    onClose()
  }

  function pickTab(t: OpenedTab) {
    navigate(`/profile/${encodeURIComponent(t.handle)}`)
    onClose()
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!q.trim()) return
    if (postSearch) {
      navigate(`/tabs-search?q=${encodeURIComponent(q.trim())}&scope=cached`)
      onClose()
      return
    }
    if (hits[0]) pickActor(hits[0])
  }

  const recent = [...tabs].sort((a, b) => b.addedAt - a.addedAt)

  return (
    <div className="overlay-panel fixed inset-0 z-50 flex flex-col">
      <div className="surface-bar pt-safe shrink-0 border-b border-white/10">
        <form onSubmit={onSubmit} className="flex h-12 items-stretch">
          <span className="grid w-10 shrink-0 place-items-center text-sm text-ink-500">⌕</span>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
            placeholder={postSearch ? 'search post text… (press ↵)' : 'search handles…'}
            className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-ink-500"
          />
          <button
            type="button"
            onClick={onClose}
            className="grid h-full w-12 shrink-0 place-items-center text-lg text-ink-100 hover:bg-white/10"
            title="close search"
          >
            ✕
          </button>
        </form>
        <div className="flex items-center gap-2 px-3 pb-2">
          <button
            type="button"
            onClick={() => setPostSearch((v) => !v)}
            className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold"
            style={{
              borderColor: postSearch ? 'var(--accent-strong)' : 'rgba(255,255,255,0.14)',
              color: postSearch ? 'var(--accent-strong)' : '#9aa4b2',
              background: postSearch ? 'rgba(56,189,248,0.10)' : 'transparent',
            }}
            title="post search: full-text search across the cached POSTS of every account (opens the results page). turn off to jump to a handle instead."
          >
            {postSearch ? '◉' : '◯'} post search
          </button>
          {postSearch ? (
            <span className="truncate text-[10px] text-ink-400">
              press ↵ to search cached posts
            </span>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {q.trim() && !postSearch ? (
          <div className="p-1">
            {searching && hits.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-ink-400">searching…</div>
            ) : null}
            {!searching && hits.length === 0 ? (
              <div className="px-3 py-2 text-[11px] text-ink-400">no matches</div>
            ) : null}
            {hits.map((r) => (
              <button
                type="button"
                key={r.did}
                onClick={() => pickActor(r)}
                className="flex w-full items-center gap-2 p-2 text-left text-sm hover:bg-white/10"
              >
                {r.avatar ? (
                  <img src={r.avatar} alt="" className="h-8 w-8 object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="h-8 w-8 bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
                )}
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-white">{r.displayName ?? '@' + r.handle}</span>
                  <span className="block truncate text-[11px] text-ink-400">@{r.handle}</span>
                </span>
              </button>
            ))}
          </div>
        ) : !q.trim() ? (
          recent.length > 0 ? (
            <div className="p-1">
              <div className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-widest text-ink-400">
                recent · watched
              </div>
              {recent.map((t) => (
                <button
                  type="button"
                  key={t.handle}
                  onClick={() => pickTab(t)}
                  className="flex w-full items-center gap-2 p-2 text-left text-sm hover:bg-white/10"
                >
                  {t.avatar ? (
                    <img src={t.avatar} alt="" className="h-8 w-8 object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="h-8 w-8 bg-gradient-to-br from-sky-500/40 to-violet-500/40" />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-white">{t.displayName ?? '@' + t.handle}</span>
                    <span className="block truncate text-[11px] text-ink-400">@{t.handle}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="px-3 py-4 text-[11px] text-ink-400">
              no open tabs yet — search a handle above
            </div>
          )
        ) : null}
      </div>
    </div>
  )
}
